import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { Camera, CheckCircle } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ActivateComplete() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();

  const [tokenInfo, setTokenInfo] = useState(null); // {code, email}
  const [tokenError, setTokenError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    skills: "",
    phone: "",
    bio: "",
    location: "",
    picture_url: "",
    password: "",
    password_confirm: "",
    popia_consent: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError("Missing activation token. Please use the link from your email.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/public/activate/token/${token}`);
        setTokenInfo(data);
      } catch (err) {
        setTokenError(formatApiError(err));
      }
    })();
  }, [token]);

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("file", file);
      const { data } = await api.post("/public/activate/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, picture_url: data.url }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim()) return setError("Full name is required");
    const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (skills.length === 0) return setError("Add at least one skill");
    if (!/^0\d{9}$/.test(form.phone)) return setError("Phone must be 10 digits starting with 0");
    if (!form.picture_url) return setError("A photo is required");
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    if (form.password !== form.password_confirm) return setError("Passwords don't match");
    if (!form.popia_consent) return setError("Please tick the POPIA consent box");

    setSubmitting(true);
    try {
      const { data } = await api.post("/public/activate/complete", {
        token,
        password: form.password,
        full_name: form.full_name,
        skills,
        phone: form.phone,
        bio: form.bio,
        location: form.location,
        picture_url: form.picture_url,
        popia_consent: form.popia_consent,
      });
      // Auto-login: store the token returned by the backend.
      if (data.token) localStorage.setItem("cc_token", data.token);
      nav(`/c/${data.code}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenError) {
    return (
      <div>
        <Nav />
        <section className="border-b border-black bg-[color:var(--cc-yellow)]">
          <div className="max-w-md mx-auto px-4 py-16 text-center">
            <h1 className="font-display font-black text-3xl">Activation link invalid</h1>
            <p className="mt-3 text-neutral-800">{tokenError}</p>
            <p className="mt-4 text-sm text-neutral-700">Links expire after 60 minutes. Open <span className="font-mono-cc">/c/&lt;your-code&gt;</span> on your phone again to start over.</p>
          </div>
        </section>
      </div>
    );
  }

  if (!tokenInfo) {
    return <div><Nav /><div className="px-4 py-20 text-center label-tag">Loading…</div></div>;
  }

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
          <div className="label-tag text-neutral-500">Activating</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-1">
            Card #{tokenInfo.code}
          </h1>
          <div className="mt-1 label-tag text-neutral-700">{tokenInfo.email}</div>

          <form onSubmit={submit} className="mt-8 space-y-4 bg-white border border-black p-5" data-testid="activate-complete-form">
            <div>
              <label className="label-tag">Full name</label>
              <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="ac-name" required />
            </div>
            <div>
              <label className="label-tag">Skills (comma separated, e.g. plumber, painter)</label>
              <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} data-testid="ac-skills" required />
            </div>
            <div>
              <label className="label-tag">Your phone (kept private)</label>
              <input
                className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })}
                maxLength={10}
                inputMode="numeric"
                placeholder="0821234567"
                data-testid="ac-phone"
                required
              />
              <div className="label-tag text-neutral-500 mt-1">Visitors never see this. We use it only to relay leads to you on WhatsApp.</div>
            </div>
            <div>
              <label className="label-tag">Location (e.g. Bellville, Cape Town)</label>
              <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="ac-location" />
            </div>
            <div>
              <label className="label-tag">Short bio (optional)</label>
              <textarea rows={3} className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="ac-bio" />
            </div>
            <div>
              <label className="label-tag">Your photo (required)</label>
              <div className="mt-2 flex items-start gap-3">
                <div className="w-24 h-24 border border-black bg-[color:var(--cc-bg-2)] grid place-items-center overflow-hidden flex-shrink-0">
                  {form.picture_url ? (
                    <img src={`${BACKEND}${form.picture_url}`} alt="" className="w-full h-full object-cover" data-testid="ac-photo-preview" />
                  ) : (
                    <Camera size={28} weight="bold" className="text-neutral-400" />
                  )}
                </div>
                <label className="label-tag px-4 py-3 border border-black brutal-btn cursor-pointer inline-flex items-center gap-2">
                  <Camera size={14} weight="bold" />
                  {uploading ? "Uploading…" : "Take / upload photo"}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                    data-testid="ac-photo-input"
                  />
                </label>
              </div>
            </div>
            <div className="border-t pt-4">
              <label className="label-tag">Set a password</label>
              <input type="password" className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ac-password" required />
            </div>
            <div>
              <label className="label-tag">Confirm password</label>
              <input type="password" className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.password_confirm} onChange={(e) => setForm({ ...form, password_confirm: e.target.value })} data-testid="ac-password-confirm" required />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={form.popia_consent} onChange={(e) => setForm({ ...form, popia_consent: e.target.checked })} data-testid="ac-popia" required />
              <span>I consent to my details being shown publicly on Curb Crew (POPIA). My phone number remains private and is only used to relay leads to me.</span>
            </label>
            {error && <div className="label-tag text-red-600" data-testid="ac-error">{error}</div>}
            <button type="submit" disabled={submitting} className="w-full bg-[color:var(--cc-yellow)] label-tag px-4 py-4 brutal-btn flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ac-submit">
              <CheckCircle size={16} weight="bold" /> {submitting ? "Activating…" : "Activate my card"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
